import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useLogPtoForEmployee } from '@/features/pto/hooks';
import {
  BEREAVEMENT_RELATIONSHIPS,
  countWeekdays,
  type BereavementRelationship,
} from '@/features/pto/format';
import type { PtoLeaveType, PtoStatus } from '@/types/database';

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export interface LogPtoPerson {
  id: string;
  full_name: string | null;
  email: string;
}

interface LogPtoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** People an approver can log PTO for (active, non-lender). */
  people: LogPtoPerson[];
  /** Optionally pre-select a person (e.g. opened from their row). */
  defaultProfileId?: string | null;
}

/**
 * Approver tool: add time off on behalf of an employee (no request required).
 * Supports PTO (counts against balance) and bereavement (does not). Defaults
 * to an "Approved" record so managers can backfill time already taken.
 */
export function LogPtoDialog({
  open,
  onOpenChange,
  people,
  defaultProfileId,
}: LogPtoDialogProps) {
  const [profileId, setProfileId] = useState('');
  const [leaveType, setLeaveType] = useState<PtoLeaveType>('pto');
  const [relationship, setRelationship] = useState<BereavementRelationship | ''>(
    '',
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [days, setDays] = useState('');
  const [daysTouched, setDaysTouched] = useState(false);
  const [status, setStatus] = useState<PtoStatus>('approved');
  const [reason, setReason] = useState('');

  const log = useLogPtoForEmployee();
  const isBereavement = leaveType === 'bereavement';

  useEffect(() => {
    if (!open) return;
    const today = todayKey();
    setProfileId(defaultProfileId ?? '');
    setLeaveType('pto');
    setRelationship('');
    setStartDate(today);
    setEndDate(today);
    setDays('1');
    setDaysTouched(false);
    setStatus('approved');
    setReason('');
  }, [open, defaultProfileId]);

  // Auto-fill day count from the weekday span until the user overrides it.
  useEffect(() => {
    if (daysTouched) return;
    const weekdays = countWeekdays(startDate, endDate);
    if (weekdays > 0) setDays(String(weekdays));
  }, [startDate, endDate, daysTouched]);

  const numericDays = Number(days);

  const save = async (): Promise<boolean> => {
    if (!profileId) {
      toast.error('Choose an employee.');
      return false;
    }
    if (!startDate || !endDate) {
      toast.error('Pick a start and end date.');
      return false;
    }
    if (new Date(endDate) < new Date(startDate)) {
      toast.error('The end date must be on or after the start date.');
      return false;
    }
    if (!Number.isFinite(numericDays) || numericDays <= 0) {
      toast.error(
        isBereavement
          ? 'Enter how many bereavement days this covers.'
          : 'Enter how many PTO days this uses.',
      );
      return false;
    }
    if (isBereavement && !relationship) {
      toast.error('Select the relationship to the employee.');
      return false;
    }
    try {
      await log.mutateAsync({
        profile_id: profileId,
        start_date: startDate,
        end_date: endDate,
        days: numericDays,
        reason: reason.trim() || null,
        leave_type: leaveType,
        bereavement_relationship: isBereavement ? relationship : null,
        status,
      });
      toast.success(isBereavement ? 'Bereavement leave added.' : 'PTO added.');
      return true;
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add time off.');
      return false;
    }
  };

  const handleSubmit = async () => {
    if (await save()) onOpenChange(false);
  };

  /** Save, then clear the employee so the next person can be entered quickly. */
  const handleSaveAndAddAnother = async () => {
    if (!(await save())) return;
    setProfileId('');
    setReason('');
    setRelationship('');
    // Keep dates / days / status / leave type for rapid backfill across people.
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add PTO</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="log-employee">Employee</Label>
            <Select value={profileId} onValueChange={setProfileId}>
              <SelectTrigger id="log-employee">
                <SelectValue placeholder="Select an employee…" />
              </SelectTrigger>
              <SelectContent>
                {people.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.full_name ?? p.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="log-leave-type">Leave type</Label>
            <Select
              value={leaveType}
              onValueChange={(v) => {
                const next = v as PtoLeaveType;
                setLeaveType(next);
                if (next !== 'bereavement') setRelationship('');
              }}
            >
              <SelectTrigger id="log-leave-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pto">PTO</SelectItem>
                <SelectItem value="bereavement">Bereavement</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isBereavement && (
            <div className="grid gap-1.5">
              <Label htmlFor="log-relationship">Relationship to employee</Label>
              <Select
                value={relationship || undefined}
                onValueChange={(v) =>
                  setRelationship(v as BereavementRelationship)
                }
              >
                <SelectTrigger id="log-relationship">
                  <SelectValue placeholder="Select relationship…" />
                </SelectTrigger>
                <SelectContent>
                  {BEREAVEMENT_RELATIONSHIPS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="log-start">Start date</Label>
              <Input
                id="log-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="log-end">End date</Label>
              <Input
                id="log-end"
                type="date"
                min={startDate || undefined}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="log-days">
                {isBereavement ? 'Days off' : 'PTO days used'}
              </Label>
              <Input
                id="log-days"
                type="number"
                min="0.5"
                step="0.5"
                value={days}
                onChange={(e) => {
                  setDaysTouched(true);
                  setDays(e.target.value);
                }}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="log-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as PtoStatus)}
              >
                <SelectTrigger id="log-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">
                    Approved (already taken / confirmed)
                  </SelectItem>
                  <SelectItem value="pending">Pending (needs review)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="log-reason">
              {isBereavement ? 'Notes (optional)' : 'Reason (optional)'}
            </Label>
            <Textarea
              id="log-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isBereavement ? 'Additional context…' : 'e.g. Vacation'}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveAndAddAnother}
            disabled={log.isPending}
          >
            {log.isPending ? 'Saving…' : 'Save & add another'}
          </Button>
          <Button onClick={handleSubmit} disabled={log.isPending}>
            {log.isPending ? 'Saving…' : 'Add PTO'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
